/**
 * TrustGate Console - Intent Record Details
 * 
 * Structured display of Bound Intent data organized by governance sections
 * Right-side panel showing Intent, Context, Governance, and Execution details
 */

import React from 'react';
import { formatTimestamp, formatCurrency } from '../../data/TrustGateDataService';
import type { TrustGateEvent } from '../../ecs/types';

interface IntentRecordDetailsProps {
  event: TrustGateEvent;
  onViewReceipt: (type: 'binding' | 'execution') => void;
  onViewPolicyDiff?: () => void;
  isAdmin?: boolean;
}

interface DetailSection {
  title: string;
  icon: string;
  fields: Array<{ label: string; value: string | React.ReactNode; mono?: boolean }>;
}

export const IntentRecordDetails: React.FC<IntentRecordDetailsProps> = ({
  event,
  onViewReceipt,
  onViewPolicyDiff,
}) => {
  const sections: DetailSection[] = [];

  // Section 1: Intent
  sections.push({
    title: 'Intent',
    icon: '🎯',
    fields: [
      { label: 'Intent Type', value: event.type || 'action_request' },
      { label: 'Target', value: (event as any).target || '/payments/transfer' },
      { label: 'Entity ID', value: event.entityId, mono: true },
      { 
        label: 'Parameters', 
        value: (
          <button
            onClick={() => {
              const params = {
                amount: (event as any).amount || 0,
                currency: (event as any).currency || 'USD',
                destination: (event as any).destination || 'N/A',
              };
              alert(JSON.stringify(params, null, 2));
            }}
            className="text-[#00d4aa] hover:text-[#00a8cc] text-sm underline"
          >
            View JSON
          </button>
        )
      },
    ],
  });

  // Section 2: Context
  const amount = (event as any).amount;
  const currency = (event as any).currency || 'USD';
  const destination = (event as any).destination;
  const memo = (event as any).memo;
  
  sections.push({
    title: 'Context',
    icon: '📝',
    fields: [
      ...(amount ? [{ label: 'Amount', value: formatCurrency(amount, currency) }] : []),
      ...(destination ? [{ label: 'Destination', value: destination, mono: true }] : []),
      ...(memo ? [{ label: 'Memo', value: memo }] : []),
      { label: 'Submitted By', value: (event as any).requestedByEmail || 'Unknown' },
      { label: 'Submitted At', value: formatTimestamp(event.timestamp) },
    ],
  });

  // Section 3: Governance
  const hasBinding = event.status === 'APPROVED' || event.status === 'ALLOW' || event.status === 'EXECUTED';
  const bindingPolicyVersion = (event as any).policyVersion || 'v3.2.1';
  const currentPolicyVersion = 'v3.4.0'; // In production, fetch current policy version
  const policyHasEvolved = bindingPolicyVersion !== currentPolicyVersion;
  
  sections.push({
    title: 'Governance',
    icon: '🔐',
    fields: [
      { 
        label: 'Authority Source', 
        value: (event as any).authority || 'trustgate_admin claim',
        mono: true 
      },
      { 
        label: 'Policy Version at Binding', 
        value: bindingPolicyVersion
      },
      ...(policyHasEvolved ? [{
        label: 'Current Policy Version',
        value: currentPolicyVersion
      }] : []),
      ...(event.decision ? [{ 
        label: 'Decision', 
        value: event.decision === 'ALLOW' ? '✓ ALLOW' : event.decision === 'DENY' ? '✕ DENY' : event.decision,
        mono: true 
      }] : []),
      ...(policyHasEvolved && onViewPolicyDiff ? [{
        label: 'Policy Evolution',
        value: (
          <button
            onClick={onViewPolicyDiff}
            className="px-3 py-1.5 bg-[#3b82f6] hover:bg-[#2563eb] text-white text-sm rounded transition-colors flex items-center gap-2"
          >
            <span>📜</span>
            <span>View Policy Changes</span>
          </button>
        )
      }] : []),
      ...(hasBinding && event.proofId ? [{ 
        label: 'Binding Receipt',
        value: (
          <button
            onClick={() => onViewReceipt('binding')}
            className="text-[#00d4aa] hover:text-[#00a8cc] text-sm underline"
          >
            View Receipt
          </button>
        )
      }] : []),
    ],
  });

  // Section 4: Execution (if applicable)
  if (event.status === 'EXECUTED') {
    sections.push({
      title: 'Execution',
      icon: '⚡',
      fields: [
        { 
          label: 'Executed At', 
          value: formatTimestamp((event as any).executionTimestamp || (event as any).updatedAt || event.timestamp)
        },
        { 
          label: 'Execution Receipt',
          value: (
            <button
              onClick={() => onViewReceipt('execution')}
              className="text-[#00d4aa] hover:text-[#00a8cc] text-sm underline"
            >
              View Receipt
            </button>
          )
        },
      ],
    });
  }

  return (
    <div className="p-6">
      <h3 className="text-lg font-semibold text-[#f8fafc] mb-6">Bound Intent Record</h3>

      <div className="space-y-6">
        {sections.map((section, sectionIndex) => (
          <div 
            key={sectionIndex}
            className="bg-white/5 border border-white/10 rounded-lg p-5"
          >
            {/* Section header */}
            <h4 className="text-base font-semibold text-[#f8fafc] mb-4 flex items-center gap-2">
              <span>{section.icon}</span>
              <span>{section.title}</span>
            </h4>

            {/* Section fields */}
            <div className="space-y-3">
              {section.fields.map((field, fieldIndex) => (
                <div 
                  key={fieldIndex}
                  className="flex justify-between items-start gap-4 pb-3 border-b border-white/5 last:border-0 last:pb-0"
                >
                  <span className="text-sm text-[#cbd5e1] min-w-[140px]">
                    {field.label}
                  </span>
                  <span className={`text-sm text-[#f8fafc] text-right flex-1 ${field.mono ? 'font-mono' : ''}`}>
                    {field.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Policy Evolution Notice (if applicable) */}
      {policyHasEvolved && (
        <div className="mt-6 p-4 bg-[#3b82f6]/10 border border-[#3b82f6]/30 rounded-lg">
          <div className="text-sm text-[#cbd5e1] flex items-start gap-2">
            <span className="text-[#3b82f6]">🔄</span>
            <div>
              <strong className="text-[#f8fafc]">Governance Has Evolved</strong>
              <div className="text-xs mt-1">
                This intent was bound under policy {bindingPolicyVersion}, but governance has since 
                evolved to {currentPolicyVersion}. The historical binding remains valid.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Read-only notice */}
      <div className="mt-6 p-4 bg-white/5 border border-white/10 rounded-lg">
        <div className="text-sm text-[#cbd5e1] flex items-start gap-2">
          <span className="text-[#f59e0b]">🔒</span>
          <div>
            <strong className="text-[#f8fafc]">Read-Only Record</strong>
            <div className="text-xs mt-1">
              Governance is not interactive post-binding. This is an immutable audit record.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
